import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type HeaderPanelId = string | null;

type HeaderPanelsState = {
	activePanelId: HeaderPanelId;
	anchorEl: HTMLElement | null;
};

type HeaderPanelsContextValue = HeaderPanelsState & {
	getIsPanelOpen: (id: string) => boolean;
	togglePanel: (id: string, anchorEl: HTMLElement | null) => void;
	openPanel: (id: string, anchorEl: HTMLElement | null) => void;
	closePanel: () => void;
};

type HeaderPanelsProviderProps = { children: ReactNode };

const HeaderPanelsContext = createContext<HeaderPanelsContextValue | undefined>(undefined);

export const HeaderPanelsProvider = ({ children }: HeaderPanelsProviderProps) => {
	const [state, setState] = useState<HeaderPanelsState>({ activePanelId: null, anchorEl: null });

	/**
	 * Forces a panel open, replacing whichever one is active.
	 */
	const openPanel = useCallback((id: string, anchorEl: HTMLElement | null) => {
		setState({ activePanelId: id, anchorEl });
	}, []);

	const closePanel = useCallback(() => {
		setState({ activePanelId: null, anchorEl: null });
	}, []);

	const togglePanel = useCallback((id: string, anchorEl: HTMLElement | null) => {
		setState((prevState) => {
			const { activePanelId: currentPanelId } = prevState;
			const isSamePanel = currentPanelId === id;

			if (isSamePanel) {
				return { activePanelId: null, anchorEl: null };
			}

			if (currentPanelId !== null) {
				return prevState;
			}

			return { activePanelId: id, anchorEl };
		});
	}, []);
	const getIsPanelOpen = useCallback(
		(id: string) => state.activePanelId === id,
		[state.activePanelId]
	);

	const value = useMemo<HeaderPanelsContextValue>(
		() => ({
			activePanelId: state.activePanelId,
			anchorEl: state.anchorEl,
			openPanel,
			closePanel,
			togglePanel,
			getIsPanelOpen,
		}),
		[state.activePanelId, state.anchorEl, openPanel, closePanel, togglePanel, getIsPanelOpen]
	);

	return <HeaderPanelsContext.Provider value={value}>{children}</HeaderPanelsContext.Provider>;
};

export const useHeaderPanels = () => {
	const context = useContext(HeaderPanelsContext);

	if (!context) {
		throw new Error('useHeaderPanels must be used inside HeaderPanelsProvider');
	}

	return context;
};
